// --- React
import React, { useState, useRef, useEffect } from "react";
// --- UI Components
import Input from "@dtci/spellbook/Input";

/**
 * EditableNumberRenderer Component
 *
 * Cell renderer for AG Grid that shows a number input when in edit mode,
 * or formatted number when not editing.
 *
 * @param {Function} getEditingRows - Function that returns current editing rows state
 * @param {Function} onValueChange - Callback when value changes (data, fieldName, newValue)
 * @param {string} fieldName - The field name being edited
 * @param {Object} options - Optional config: { decimalScale, suffix, prefix }
 * @returns {Function} The cell renderer component
 */
export const EditableNumberRenderer = (
  getEditingRows,
  onValueChange,
  fieldName,
  options = {},
) => {
  const { decimalScale = 2, suffix = "", prefix = "" } = options;

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return `${prefix}${num.toFixed(decimalScale)}${suffix}`;
  };

  const Renderer = ({ value, data }) => {
    const editingRows = getEditingRows ? getEditingRows() : {};
    const isEditing = editingRows[data?.id];

    // Display value without suffix in input for easier editing
    const rawValue =
      value !== null && value !== undefined
        ? String(value).replace(suffix, "").replace(prefix, "")
        : "";

    // Use local state for smooth typing - initialized once when entering edit mode
    const [localValue, setLocalValue] = useState(rawValue);
    const initializedRef = useRef(false);

    // Reset local state when entering edit mode
    useEffect(() => {
      if (isEditing && !initializedRef.current) {
        setLocalValue(rawValue);
        initializedRef.current = true;
      } else if (!isEditing) {
        initializedRef.current = false;
      }
    }, [isEditing, rawValue]);

    if (!isEditing) {
      return <span>{formatNumber(value)}</span>;
    }

    const handleChange = (e) => {
      let newValue = e.target.value;

      // Remove suffix/prefix for storage
      if (suffix) {
        newValue = newValue.replace(suffix, "");
      }
      if (prefix) {
        newValue = newValue.replace(prefix, "");
      }

      setLocalValue(newValue);
    };

    // Only sync to parent on blur to prevent grid scroll issues
    const handleBlur = () => {
      if (onValueChange && localValue !== rawValue) {
        onValueChange(data, fieldName, localValue);
      }
    };

    return (
      <Input
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        width="auto"
        type="number"
        style={{ minWidth: 100 }}
      />
    );
  };

  return Renderer;
};
